import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { onSnapshot, doc } from "firebase/firestore";
import { firestore } from "../firebase";
import { CollabDataContext } from "./collabDataContext";
import { MarketplaceOrdersContext } from "./MarketplaceOrdersContext";

export const ConversationsContext = createContext<Record<string, any>>({});

export const ConversationsProvider = ({ children }: { children: React.ReactNode }) => {
    const collabs = useContext(CollabDataContext);
    const { orders: marketplaceOrders } = useContext(MarketplaceOrdersContext);
    const [conversations, setConversations] = useState<Record<string, any>>({});
    const trackedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const collabIds = collabs.map((c) => c.id);
    const orderIds = marketplaceOrders.map((o) => o.id);
    const mergedIds = new Set([...collabIds, ...orderIds]);

    const prevTracked = trackedIdsRef.current;
    const removed = [...prevTracked].filter((id) => !mergedIds.has(id));
    if (removed.length > 0) {
      setConversations((prevConv) => {
        const next = { ...prevConv };
        removed.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }
    trackedIdsRef.current = mergedIds;

    if (mergedIds.size === 0) {
      setConversations({});
      return;
    }

    const orderIdSet = new Set(orderIds);
    const unsubscribes: Array<() => void> = [];
    mergedIds.forEach((conversationId) => {
      const collectionName = orderIdSet.has(conversationId) ? "marketplaceConversations" : "conversations";
      const conversationDocRef = doc(firestore, collectionName, conversationId);
      const unsubscribe = onSnapshot(conversationDocRef, (docSnap) => {
        setConversations((prev) => {
          if (!docSnap.exists()) {
            const next = { ...prev };
            delete next[conversationId];
            return next;
          }
          const data = docSnap.data();
          return {
            ...prev,
            [conversationId]: { id: conversationId, ...data },
          };
        });
      });

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [collabs, marketplaceOrders]);

  return (
    <ConversationsContext.Provider value={conversations ?? {}}>
      {children}
    </ConversationsContext.Provider>
  );
};

export default ConversationsProvider;
